// Pestaña Precio: precio vigente, registrar uno nuevo (retail.product.update) e historial.
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { Button, Input } from '@/kit';
import { useIdempotentAttempt } from '@/hooks/useIdempotentAttempt';
import { useCreateSkuPrice } from '../../api/useCatalogWrites';
import { useSkuPrices } from '../../api/useInventorySku';
import {
    EMPTY_PRICE_FORM,
    priceFormSchema,
    toCreateSkuPriceRequest,
    type PriceForm,
} from '../../lib/catalogForms';
import { useInventoryAbilities } from '../../lib/inventoryPermissions';
import { applyWriteError } from '../../lib/inventoryWriteErrors';
import { WriteErrorNotice } from '../catalog/FormBits';
import { DetailCard } from './DetailCard';
import {
    IntegrationLockNotice,
    useIntegrationLock,
} from '../../../integration/components/IntegrationLock';
import { isIntegrationFreeKind } from '../../../integration/lib/integrationLock';
import { PriceHistoryContent, priceOrDash } from './PriceHistoryModal';

function CurrentPrice({ label, value }: { label: string; value: string }) {
    return (
        <div className="flex flex-col gap-0.5">
            <span className="text-[9.5px] font-bold uppercase tracking-[0.04em] text-muted-foreground">
                {label}
            </span>
            <span className="text-[18px] font-bold tabular-nums">{value}</span>
        </div>
    );
}

export function RegisterPriceForm({ sku }: { sku: string }) {
    const create = useCreateSkuPrice(sku);
    const attempt = useIdempotentAttempt(`price:${sku}`);
    const [error, setError] = useState<string | null>(null);
    const form = useForm<PriceForm>({
        resolver: zodResolver(priceFormSchema),
        defaultValues: EMPTY_PRICE_FORM,
    });
    const { errors } = form.formState;
    return (
        <form
            noValidate
            aria-label="Registrar precio"
            className="flex flex-col gap-3"
            onSubmit={(event) => {
                void form.handleSubmit(async (values) => {
                    const body = toCreateSkuPriceRequest(values);
                    setError(null);
                    try {
                        await attempt.run(body, (key) => create.mutateAsync({ body, key }));
                        toast.success('Precio registrado', { description: sku });
                        form.reset(EMPTY_PRICE_FORM);
                    } catch (failure) {
                        setError(applyWriteError(failure, attempt, body).message);
                    }
                })(event);
            }}
        >
            <Input
                label="Precio contado"
                required
                inputMode="decimal"
                placeholder="0.00"
                error={errors.basePrice?.message}
                {...form.register('basePrice')}
            />
            <p className="text-[10.5px] text-muted-foreground">
                Cada precio es un registro nuevo: el anterior queda en el historial y la venta toma
                el más reciente.
            </p>
            <WriteErrorNotice message={error} />
            <div className="flex justify-end">
                <Button type="submit" size="sm" isLoading={create.isPending}>
                    Registrar precio
                </Button>
            </div>
        </form>
    );
}

/** `kind` es el de la fila: kits y servicios registran precio aunque la cadena esté integrada. */
export function PriceTab({ sku, kind = 'PRODUCT' }: { sku: string; kind?: string }) {
    const { canUpdateProduct } = useInventoryAbilities();
    const lock = useIntegrationLock();
    const locked = lock.locked && !isIntegrationFreeKind(kind);
    const prices = useSkuPrices(sku, true);
    const current = prices.data?.items.at(0);
    return (
        <>
            <DetailCard title="Precio vigente">
                <CurrentPrice label="Contado" value={priceOrDash(current?.basePriceCents)} />
            </DetailCard>
            {canUpdateProduct && !locked ? (
                <DetailCard title="Registrar precio" aside="Origen SureKeep">
                    <RegisterPriceForm sku={sku} />
                </DetailCard>
            ) : null}
            {canUpdateProduct && locked ? (
                <DetailCard title="Registrar precio">
                    <IntegrationLockNotice message="lockedPrice" />
                </DetailCard>
            ) : null}
            <DetailCard title="Historial de precios" aside="Más reciente primero">
                <PriceHistoryContent prices={prices} />
            </DetailCard>
        </>
    );
}
